import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('mini-selling-plan:price-change', this.updateMiniPlanPrice);
    this.syncFromMiniWidget();
    window.setTimeout(() => this.syncFromMiniWidget(), 0);
    window.setTimeout(() => this.syncFromMiniWidget(), 150);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('mini-selling-plan:price-change', this.updateMiniPlanPrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    this.syncFromMiniWidget();
  };

  /**
   * Sync displayed price to mini selling plan selection.
   * @param {CustomEvent} event
   */
  updateMiniPlanPrice = (event) => {
    const detail = event?.detail || {};
    const eventProductId = String(detail.productId || '');
    const thisProductId = String(this.dataset.productId || '');

    if (!eventProductId || !thisProductId || eventProductId !== thisProductId) return;

    const sourceRoot = detail.sourceRoot instanceof Element ? detail.sourceRoot : null;
    if (sourceRoot) {
      const sourceScope = sourceRoot.closest('.product-card, .product-details, .shopify-section, dialog');
      const thisScope = this.closest('.product-card, .product-details, .shopify-section, dialog');
      if (sourceScope && thisScope && sourceScope !== thisScope) return;
    }

    const price = detail.price || '';
    if (!price) return;

    const compareAtPrice = this.resolveCompareAtPrice({
      fallbackCompareAt: detail.compareAtPrice || '',
    });

    this.applyPriceMarkup(price, compareAtPrice);
  };

  usesCompareAtAsStriked() {
    return this.dataset.useCompareAtAsStriked === 'true';
  }

  getMiniVariantContext() {
    const thisProductId = String(this.dataset.productId || '');
    if (!thisProductId) return null;

    const scope = this.closest('.product-card, .product-details, .shopify-section, dialog') || document;
    const miniRoot = scope.querySelector(`.add-to-cart-with-plan[data-mini-product-id="${thisProductId}"]`);
    if (!(miniRoot instanceof HTMLElement) || miniRoot.dataset.miniSellingPlanEnabled !== 'true') return null;

    const mapNode = miniRoot.querySelector('[data-mini-selling-plan-map]');
    const planMap = this.safeParseJson(mapNode?.textContent || '');
    if (!planMap) return null;

    const form = miniRoot.closest("form[action*='/cart/add']");
    const variantId = form?.querySelector("input[name='id'], select[name='id']")?.value || '';
    const variantState = planMap[String(variantId)] || null;
    if (!variantState) return null;

    const oneTimeInput = miniRoot.querySelector("[data-mini-selling-plan-purchase][value='onetime']");
    const planSelect = miniRoot.querySelector('[data-mini-selling-plan-select]');
    const selectedPlan =
      variantState.plans?.find((plan) => String(plan.id) === String(planSelect?.value || '')) ||
      variantState.plans?.[0] ||
      null;
    const isOneTime = oneTimeInput instanceof HTMLInputElement ? oneTimeInput.checked : false;

    return { variantState, selectedPlan, isOneTime };
  }

  resolveCompareAtPrice({ fallbackCompareAt = '' } = {}) {
    const context = this.getMiniVariantContext();
    const variantState = context?.variantState;
    const isOneTime = context?.isOneTime;
    const selectedPlan = context?.selectedPlan;

    if (this.usesCompareAtAsStriked()) {
      return variantState?.shopify_compare_at_price || '';
    }

    if (!context) return fallbackCompareAt;

    if (isOneTime) {
      return variantState.one_time_compare_at_price || fallbackCompareAt || '';
    }

    return selectedPlan?.compare_at_price || fallbackCompareAt || '';
  }

  syncFromMiniWidget() {
    const context = this.getMiniVariantContext();
    if (!context) return;

    const { variantState, selectedPlan, isOneTime } = context;
    const price = isOneTime ? variantState.one_time_price : selectedPlan?.price;
    if (!price) return;

    const compareAtPrice = this.resolveCompareAtPrice();
    this.applyPriceMarkup(price, compareAtPrice || '');
  }

  safeParseJson(text) {
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  applyPriceMarkup(price, compareAtPrice) {
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;

    const currentRegularLabel =
      currentPrice.querySelector('.compare-at-price')?.closest('[role="group"]')?.querySelector('.visually-hidden')
        ?.textContent?.trim() || 'Regular price';
    const currentSaleLabel =
      currentPrice.querySelector('.price')?.closest('[role="group"]')?.querySelector('.visually-hidden')?.textContent
        ?.trim() || 'Sale price';

    const makeGroup = (labelText, valueClass, valueText) => {
      const group = document.createElement('span');
      group.setAttribute('role', 'group');

      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = `${labelText}\u00A0`;

      const value = document.createElement('span');
      value.className = valueClass;
      value.textContent = valueText;

      group.append(label, value);
      return group;
    };

    currentPrice.textContent = '';

    if (compareAtPrice && compareAtPrice !== price) {
      const compareGroup = makeGroup(currentRegularLabel, 'compare-at-price', compareAtPrice);
      const saleGroup = makeGroup(currentSaleLabel, 'price', price);
      // Switched order: compare-at first, then current price.
      currentPrice.append(compareGroup, saleGroup);
      return;
    }

    const regularPrice = document.createElement('span');
    regularPrice.className = 'price';
    regularPrice.textContent = price;
    currentPrice.appendChild(regularPrice);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
